import {InterceptorConfig} from './oauth-config';
import {HttpInterceptorFn} from '@angular/common/http';
import {inject} from '@angular/core';
import {Store} from '@ngxs/store';
import {AuthState} from './+state/state';

/**
 * Helper function to match URL patterns
 */
function matchesPattern(url: string, pattern: string | RegExp): boolean {
  if (pattern instanceof RegExp) {
    return pattern.test(url);
  }

  // Convert wildcard patterns to regex
  if (pattern.includes('*')) {
    const regexPattern = pattern
      .replace(/[.+?^${}()|[\]\\]/g, '\\$&') // Escape regex special chars
      .replace(/\*/g, '.*'); // Replace * with .*
    return new RegExp(`^${regexPattern}$`).test(url);
  }

  // Exact match or starts with
  return url === pattern || url.startsWith(pattern);
}

/**
 * Create an auth interceptor with custom configuration
 */
export function createAuthInterceptor(config: InterceptorConfig): HttpInterceptorFn {
  return (req, next) => {
    const store = inject(Store);

    // Check if URL should be excluded
    if (config.excludePatterns?.some(pattern => matchesPattern(req.url, pattern))) {
      return next(req);
    }

    // Check if URL matches any include pattern
    const shouldInclude = config.urlPatterns.some(pattern => matchesPattern(req.url, pattern));

    if (!shouldInclude) {
      return next(req);
    }

    // Get auth header from state
    const token = store.selectSnapshot(AuthState.accessToken);
    const tokenType = store.selectSnapshot(AuthState.tokenType);

    if (!token) {
      return next(req);
    }

    // Construct header value
    const headerName = config.headerName || 'Authorization';
    const tokenPrefix = config.tokenPrefix !== undefined ? config.tokenPrefix : tokenType;
    const headerValue = tokenPrefix ? `${tokenPrefix} ${token}` : token;

    // Clone request and add header
    const authReq = req.clone({
      headers: req.headers.set(headerName, headerValue)
    });

    return next(authReq);
  };
}
